import React, { useCallback, useState, useEffect } from "react";
import * as Yup from "yup";
import { v4 as uuid } from "uuid";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Auth, Hub } from "aws-amplify";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";
import { useQueryParams } from "../../helpers";
import {
  EMPTY_STRING,
  ROUTES,
  CACHE_KEYS,
  MODAL_TIMEOUT,
  USER_INFO,
  ZERO,
} from "../../constants";
import { loginBg } from "../../assets/images";
import Alert, { selectAlert } from "../../components/organisms/Alert";
import { logoIcon } from "../../assets/icons";
import { saveUser, updateInvitations } from "../Users";
import { fetchUserProfileById, updateUserProfile } from "../UserProfile";
import { actions } from "../UserProfile/userProfile.reducers";
import { actions as authScreensActions } from "./authScreens.reducers";
import Spinner from "../../components/organisms/Spinner";
import { OtpInput } from "../../components/molecules";

const USER_EMAIL = "email";
const { LOGIN } = ROUTES;
const { X_REQUEST_ID, ORGANIZATION } = CACHE_KEYS;
const { setShouldShowNav } = authScreensActions;
const { setAwsToken, setShouldFetchUser, setIsAuthenticated } = actions;
const SIGNUP_ERROR_MESSAGE =
  "Unable to create account. Please try after sometime.";
const SUCCESS = "SUCCESS";
const RESEND_OTP_TIMEOUT_SECONDS = 60;
const ACCEPTED = "ACCEPTED";

const AuthConfirmation = () => {
  const queryParams = useQueryParams();
  const dispatch = useDispatch();
  const getUserEmail = queryParams.get(USER_EMAIL);
  const decodedEmailURI = decodeURIComponent(getUserEmail);
  const userInfo = JSON.parse(sessionStorage.getItem(USER_INFO));

  const selectedAlert = useSelector(selectAlert);
  const [alertData, setAlertData] = useState(selectedAlert);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [seconds, setSeconds] = useState(ZERO);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpValid, setIsOtpValid] = useState(true);

  const userDetails = {
    authCode: {
      one: EMPTY_STRING,
      two: EMPTY_STRING,
      three: EMPTY_STRING,
      four: EMPTY_STRING,
      five: EMPTY_STRING,
      six: EMPTY_STRING,
    },
  };

  const validationSchema = Yup.object().shape({
    authCode: Yup.object().shape(
      Object.keys(userDetails.authCode).reduce(
        (acc, val) => ({ ...acc, [val]: Yup.string().required() }),
        {}
      )
    ),
  });

  const createUser = useCallback(
    (user) => {
      dispatch(setShouldFetchUser(false));
      dispatch(saveUser(user)).then((res) => {
        const { payload } = res;
        if (!isEmpty(payload)) {
          const organization = {
            id: payload.organizationId,
            name: payload.organizationName,
          };
          sessionStorage.setItem(ORGANIZATION, JSON.stringify(organization));
          dispatch(setShouldShowNav(true));
          dispatch(setIsAuthenticated(true))
          dispatch(fetchUserProfileById(res.payload.id));
        } else setAlertData({ message: SIGNUP_ERROR_MESSAGE });
      });
    },
    [dispatch]
  );

  const listenToAutoSignInEvent = useCallback(() => {
    try {
      Hub.listen("auth", ({ payload }) => {
        const { event } = payload;
        if (event === "autoSignIn") {
          const user = payload.data;
          // 'user' have the description of auto-signIn user
          dispatch(setAwsToken(user.signInUserSession.accessToken.jwtToken));
          if (userInfo.inviteId) {
            dispatch(
              updateUserProfile({
                id: userInfo.userId,
                user: {
                  sub: userInfo.sub,
                },
              })
            ).then(() => {
              dispatch(
                updateInvitations({
                  id: userInfo.inviteId,
                  user: {
                    status: ACCEPTED,
                  },
                })
              );
              dispatch(setShouldShowNav(true));
              dispatch(setIsAuthenticated(true));
            });
          } else {
            delete userInfo.inviteId;
            delete userInfo.userId;
            createUser(userInfo);
          }
          sessionStorage.setItem(X_REQUEST_ID, uuid());
        } else if (event === "autoSignIn_failure") {
          // Add handling if auto signIn fails, redirect to sign in page
        }
      });
    } catch (error) {
      setIsOtpValid(false);
      setAlertData({ message: error.message });
      setIsSpinnerActive(false);
    }
  }, [dispatch, createUser, userInfo]);

  const confirmSignUp = useCallback(
    async (username, code) => {
      try {
        const response = await Auth.confirmSignUp(username, code);
        if (response === SUCCESS) listenToAutoSignInEvent();
      } catch (error) {
        setIsOtpValid(false);
        setAlertData({ message: error.message });
        setIsSpinnerActive(false);
      }
    },
    [listenToAutoSignInEvent]
  );

  const handleOnSubmit = useCallback(
    (values) => {
      try {
        setIsSpinnerActive(true);
        const { authCode } = values;
        const code = Object.values(authCode).reduce(
          (acc, val) => acc + val,
          EMPTY_STRING
        );

        if (getUserEmail) {
          confirmSignUp(decodedEmailURI, code);
        }
        if (userInfo) {
          const { email } = userInfo;
          confirmSignUp(email, code);
        }
      } catch (error) {
        setIsSpinnerActive(false);
      }
    },
    [decodedEmailURI, confirmSignUp, getUserEmail, userInfo]
  );

  const resendConfirmationCode = async (username) => {
    try {
      await Auth.resendSignUp(username);
    } catch (err) {
      setAlertData({ message: err.message });
    }
  };

  const getAuthCode = () => {
    setAlertData();
    if (getUserEmail) resendConfirmationCode(decodedEmailURI);
    if (userInfo) {
      const { email } = userInfo;
      resendConfirmationCode(email);
    }
  };

  useEffect(() => {
    if (getUserEmail) {
      sessionStorage.removeItem(USER_INFO);
      resendConfirmationCode(decodedEmailURI);
    }
  }, [decodedEmailURI, getUserEmail]);

  useEffect(() => {
    setAlertData(selectedAlert);
  }, [selectedAlert]);

  const isDisabled = (value) =>
    Object.values(value).some((val) => val === EMPTY_STRING);

  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > ZERO) setSeconds(seconds - 1);
      else if (seconds === ZERO) {
        clearInterval(interval);
        setIsOtpSent(false);
      }
    }, MODAL_TIMEOUT);
    return () => clearInterval(interval);
  }, [seconds]);

  const onClickSendOtp = () => {
    if (seconds <= ZERO) {
      setIsOtpSent(true);
      setSeconds(RESEND_OTP_TIMEOUT_SECONDS);
      getAuthCode();
    }
  };

  return (
    <>
      <div className="auth-container min-h-screen relative flex items-center">
        <img
          src={loginBg}
          className="w-full h-full absolute top-0 left-0 object-cover pointer-events-none"
          alt="background"
        />
        <div className="auth-box text-gray-50 relative w-full mx-auto py-12 px-6 sm:w-1/2 md:w-1/2 lg:w-1/3 2xl:w-1/4">
          <Formik
            initialValues={userDetails}
            validationSchema={validationSchema}
            onSubmit={handleOnSubmit}
          >
            {({ values, handleSubmit, setFieldValue, isValid }) => (
              <Form onSubmit={handleSubmit}>
                <div className="form-group mb-3">
                  <div className="auth-header mb-8">
                    {alertData && (
                      <div className="my-2">
                        <Alert
                          type={alertData.type}
                          message={alertData.message}
                          handleOnClose={setAlertData}
                        />
                      </div>
                    )}
                    <div className="logo mb-6">
                      <img src={logoIcon} width={52} alt="logo" />
                    </div>
                    <h1 className="text-3xl leading-9 font-extrabold mb-2">
                      Let&apos;s Get Started
                    </h1>
                    <span className="text-6">
                      Or&nbsp;
                      <Link
                        to={LOGIN}
                        className="text-indigo-200 hover:text-indigo-500"
                      >
                        login instead
                      </Link>
                    </span>
                  </div>
                  <label className="text-6">Confirmation Code</label>
                  <div className="my-4 flex justify-between">
                    <OtpInput
                      values={values}
                      setFieldValue={setFieldValue}
                      isOtpValid={isOtpValid}
                      setIsOtpValid={setIsOtpValid}
                    />
                  </div>
                  <div className="flex justify-end text-6">
                    Didn&apos;t get a code?
                    <span
                      className="text-indigo-200 hover:text-indigo-500 cursor-pointer"
                      onClick={onClickSendOtp}
                    >
                      {isOtpSent ? (
                        <span
                          className="text-indigo-200 hover:text-indigo-500 cursor-pointer disable:cursor-not-allowed"
                          disabled={isOtpSent}
                        >
                          <p>
                            &nbsp;Resend in:{" "}
                            {seconds < 10 ? `0${seconds}` : seconds}
                          </p>
                        </span>
                      ) : (
                        <p>&nbsp;Click to resend.</p>
                      )}
                    </span>
                  </div>
                </div>
                <button
                  className="btn btn-primary block w-full text-center my-2"
                  type="submit"
                  disabled={
                    isSpinnerActive || !isValid || isDisabled(values.authCode)
                  }
                >
                  <Spinner showSpinner={isSpinnerActive} persistSize>
                    Submit Code
                  </Spinner>
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default AuthConfirmation;
